import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import type { LoginResponse } from '@isoft-people/types';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      setSession(data.user, data.tokens.accessToken, data.tokens.refreshToken);
      if (data.user.mustChangePassword) {
        navigate('/cambiar-password');
      } else {
        navigate('/');
      }
    } catch {
      setError('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Panel izquierdo - navy gradient */}
      <div
        className="relative hidden flex-col items-center justify-center px-12 text-white lg:flex lg:w-1/2"
        style={{
          background:
            'linear-gradient(135deg, #0A1931 0%, #15294D 50%, #1363DF 100%)',
        }}
      >
        <div className="flex flex-col items-center text-center">
          <h1 className="text-[64px] font-bold leading-none tracking-[6px]">ISOFT</h1>
          <div className="mt-2 text-2xl font-light tracking-[8px] text-primary-light">
            People
          </div>
          <p className="mt-10 max-w-xs text-base font-light leading-relaxed text-white/70">
            Gestión de talento humano
            <br />
            para equipos que trascienden.
          </p>
        </div>
        <div className="absolute bottom-6 text-xs text-white/40">
          © 2026 ISOFT · Todos los derechos reservados
        </div>
      </div>

      {/* Panel derecho - form */}
      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-semibold text-font-primary">Bienvenido</h2>
          <p className="mt-2 text-sm text-font-secondary">Inicia sesión en tu cuenta</p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-font-primary">
                Correo electrónico
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input h-11"
                placeholder="tu@isoft.com"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-font-primary">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input h-11 pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-font-tertiary hover:text-font-secondary"
                  tabIndex={-1}
                >
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex h-11 items-center justify-center rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-60"
            >
              {loading ? 'Ingresando...' : 'Iniciar sesión'}
            </button>

            <a
              href="#"
              className="text-center text-sm text-primary hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </a>

            <div className="mt-6 flex justify-center">
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs text-font-secondary hover:bg-bg"
              >
                🌐 Español (ES)
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
